import {SmallLoader} from '@kit';

import React, {useCallback, useMemo} from 'react';

import {useRuleQuery} from '../../../generated/types';
import {PageTools} from '../../../components/page-tools';
import {getApolloError} from '../../../components/utils';
import {sourceBookToString} from '../../../components/enums';
import type {Source} from '../../../generated/types';
import {EditHoc} from '../../../components/edit-hoc';
import {Markdown, MarkdownImageContext, MarkdownImageProps} from '../../../components/utils/md';
import {useGalleryImages} from '../../../components/gallery';
import {WikiImage, wikiGalleryImages} from '../../wiki/view/wiki-image';
import {NotFoundSeo, isNotFoundError, useNotFound} from '../../../lib/seo';
import {Link} from '../../../lib/routes';
import {RuleSeo} from '../seo';
import {NotFound} from '../../../components/not-found';
import {OtherTitles} from '../../../components/other-titles';
import {ruleParagraph} from '../../../components/render-helpers';
import {CommentTargetType, Comments} from '../../../components/comments';

import dashboardCss from '@kit/sidebar/dashboard.module.css';
import pageCss from '../../../components/common.module.css';
import css from './rule-view.module.css';

type Props = {
	ruleId: string;
	/** название из списка: при серверной отрисовке выбранная запись ещё неизвестна, тогда берётся название из самой статьи */
	title: string;
};

function sourceText(source: Source[]) {
	const pages = new Map<string, number[]>();
	for (const {book, page} of source) {
		if (!pages.has(book)) pages.set(book, []);
		if (page) pages.get(book)!.push(page);
	}
	return [...pages]
		.map(([book, list]) => {
			const sorted = [...new Set(list)].sort((a, b) => a - b);
			return `«${sourceBookToString(book)}»${sorted.length ? `, стр. ${sorted.join(', ')}` : ''}`;
		})
		.join('; ');
}

export const RuleView: React.FC<Props> = ({ruleId, title}) => {
	const {data, loading, error} = useRuleQuery({variables: {id: ruleId}, ssr: true});
	const rule = data?.rule;
	const notFound = !loading && !rule && isNotFoundError(error);
	useNotFound(notFound);

	const images = useMemo(() => wikiGalleryImages(rule?.description || ''), [rule?.description]);
	useGalleryImages(images);
	const renderImage = useCallback((image: MarkdownImageProps) => <WikiImage image={image} />, []);

	return (
		<article className={dashboardCss.readingWrapper}>
			{rule && <RuleSeo item={rule} />}
			{notFound && <NotFoundSeo />}
			<PageTools />
			<h1 className={dashboardCss.mainTitle}>{rule?.title || title}</h1>
			<div className={dashboardCss.page}>
				{loading && (
					<div className={pageCss.fullSizeBlock}>
						<SmallLoader />
					</div>
				)}
				{notFound ? (
					<NotFound />
				) : (
					error && <div className={pageCss.fullSizeBlock}>{getApolloError(error).message}</div>
				)}
				{rule && (
					<EditHoc load={() => import('../edit-buttons').then((m) => m.EditRule)} editProps={{id: rule.id}}>
						{rule.parent && (
							<nav className={css.breadcrumbs} aria-label="Семейство">
								<Link route="rule" params={{id: rule.parent.id}}>
									{rule.parent.title}
								</Link>
							</nav>
						)}
						<MarkdownImageContext.Provider value={renderImage}>
							<Markdown text={rule.description || ''} />
						</MarkdownImageContext.Provider>
						{rule.children.length > 0 && (
							<div className={css.children}>
								{rule.children.map((child) => (
									<Markdown key={child.id} text={ruleParagraph(child.id, child.title, child.summary?.trim() || child.title)} />
								))}
							</div>
						)}
						<OtherTitles originalTitle={rule.originalTitle} alternativeTitles={rule.alternativeTitles} />
						{rule.source?.length ? <p className={css.source}>Источник: {sourceText(rule.source)}.</p> : null}
						<Comments targetType={CommentTargetType.Rule} targetId={rule.id} />
					</EditHoc>
				)}
			</div>
		</article>
	);
};
